import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPaperPlane, FaUserCircle, FaCheckCircle, FaStar, 
  FaImage, FaChevronDown, FaChartBar, FaCheck, FaClock, 
  FaThumbsUp, FaRedo, FaCommentDots, FaTimes
} from 'react-icons/fa';
import './Sandbox.scss';

type ScenarioId = 'printer' | 'pass' | 'cleaning';

interface Scenario {
  id: ScenarioId;
  category: string;
  title: string;
  manager: string;
  question: string;
  answers: string[];
  replies: string[];
  managerAvatar: string;
  scenarioImage: string;
}

const scenarios: Record<ScenarioId, Scenario> = {
  printer: {
    id: 'printer',
    category: 'IT Отдел',
    title: 'Закончилась краска в принтере',
    manager: 'Михаил Т.',
    question: 'Привет! Картридж есть в запасе в кабинете или принести со склада?',
    answers: ['Есть в запасе', 'Нужно принести'],
    replies: ['Отлично, буду через 5 минут, помогу заменить.', 'Понял, захвачу со склада 53A и подойду.'],
    managerAvatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    scenarioImage: 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=400&h=300&fit=crop'
  },
  pass: {
    id: 'pass',
    category: 'Служба Безопасности',
    title: 'Оформление гостевого пропуска',
    manager: 'Охрана (КПП)',
    question: 'Уточните, гость будет на автомобиле или пешком?',
    answers: ['На автомобиле (А777АА)', 'Пешком'],
    replies: ['Внес номер в базу, пусть заезжает на паркинг B.', 'Ок, пропуск заказан, пусть покажет уд.личности на проходной.'],
    managerAvatar: 'https://randomuser.me/api/portraits/men/44.jpg',
    scenarioImage: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop'
  },
  cleaning: {
    id: 'cleaning',
    category: 'АХО',
    title: 'Пролили кофе в переговорной',
    manager: 'Служба Клининга',
    question: 'Ковер сильно пострадал? Нужен моющий пылесос?',
    answers: ['Да, большое пятно', 'Нет, только на стол'],
    replies: ['Беру технику, буду через 3 минуты.', 'Поняла, иду с микрофиброй.'],
    managerAvatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    scenarioImage: 'https://images.unsplash.com/photo-1600093463592-8e36ae95ef56?w=400&h=300&fit=crop'
  }
};

const timelineSteps = [
  'Заявка создана',
  'Назначен ответственный',
  'Уточнение деталей',
  'В процессе',
  'Решено',
  'Фидбек получен'
];

export const Sandbox: React.FC = () => {
  // Views: 'form' -> 'chat' -> 'dashboard'
  const [view, setView] = useState<'form' | 'chat' | 'dashboard'>('form');
  
  // State for form
  const [selectedCategory, setSelectedCategory] = useState<ScenarioId | ''>('');
  const [text, setText] = useState('');
  const [hasImage, setHasImage] = useState(false);
  
  // State for Chat timeline
  // 0: submitted, 1: assigned, 2: question asked, 3: user answered, 4: resolved, 5: rated
  const [step, setStep] = useState(0);
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null);
  const [rating, setRating] = useState(0);

  const activeScenario = selectedCategory ? scenarios[selectedCategory as ScenarioId] : scenarios['printer'];

  // Auto-advance logic for initial steps
  useEffect(() => {
    if (view === 'chat' && step < 2) {
      const timer = setTimeout(() => {
        setStep(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }

    if (view === 'chat' && step === 3 && userAnswerIndex !== null) {
      // After user answers, manager replies, then resolves
      const resolveTimer = setTimeout(() => {
        setStep(4);
      }, 2500);
      return () => clearTimeout(resolveTimer);
    }
  }, [view, step, userAnswerIndex]);

  const handleStartSim = () => {
    if (!selectedCategory) return;
    if (!text.trim()) setText(activeScenario.title); // auto-fill if empty
    setView('chat');
    setStep(0);
  };

  const handleUserAnswer = (index: number) => {
    setUserAnswerIndex(index);
    setStep(3); // user answered
  };

  const resetAll = () => {
    setView('form');
    setText('');
    setHasImage(false);
    setSelectedCategory('');
    setStep(0);
    setUserAnswerIndex(null);
    setRating(0);
  };

  return (
    <section id="demo" className="sandbox section">
      <div className="container">
        <motion.div 
          className="sandbox__header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="sandbox__badge">Interactive Demo</div>
          <h2 className="sandbox__title">Магия в действии</h2>
          <p className="sandbox__subtitle">
            Отправьте заявку, ответьте на уточняющий вопрос и посмотрите реальную статистику в системном дашборде.
          </p>
        </motion.div>

        <div className="sandbox__window">
          {/* WINDOW HEADER */}
          <div className="window-topbar">
            <div className="window-dots"><span></span><span></span><span></span></div>
            <div className="window-title">
              {view === 'form' && 'Создание заявки'}
              {view === 'chat' && 'Отслеживание заявки'}
              {view === 'dashboard' && 'Дашборд Руководителя'}
            </div>
            <div style={{width: '40px'}}></div>
          </div>

          <div className="window-content">
            <AnimatePresence mode="wait">
              
              {/* VIEW 1: FORM */}
              {view === 'form' && (
                <motion.div 
                  key="form"
                  className="view-form"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h3 className="form-title">Оставить заявку</h3>
                  
                  <div className="form-group">
                    <label>Категория обращения</label>
                    <div className="select-wrapper">
                      <select 
                        value={selectedCategory} 
                        onChange={(e) => {
                          setSelectedCategory(e.target.value as ScenarioId);
                          setText(scenarios[e.target.value as ScenarioId].title);
                        }}
                      >
                        <option value="" disabled>Выберите раздел...</option>
                        {Object.values(scenarios).map(s => (
                          <option key={s.id} value={s.id}>{s.category}</option>
                        ))}
                      </select>
                      {/* @ts-ignore */}
                      <FaChevronDown className="select-icon" />
                    </div>
                  </div>

                  <div className="form-group form-group--textarea">
                    <div className="textarea-header">
                      <label>Опишите проблему</label>
                      <button 
                        className="btn-add-image"
                        onClick={(e) => { e.preventDefault(); setHasImage(true); }}
                      >
                        {/* @ts-ignore */}
                        <FaImage /> Добавить файл
                      </button>
                    </div>
                    <textarea 
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Введите ваш текст..."
                      rows={5}
                    ></textarea>
                    
                    {hasImage && (
                      <div className="attached-image-preview">
                        <img src={activeScenario.scenarioImage} alt="Preview" />
                        <button className="btn-remove-image" onClick={(e) => { e.preventDefault(); setHasImage(false); }}>
                          {/* @ts-ignore */}
                          <FaTimes />
                        </button>
                      </div>
                    )}
                  </div>

                  <button 
                    className="btn-submit-ticket" 
                    onClick={handleStartSim}
                    disabled={!selectedCategory || !text.trim()}
                    style={{ 
                      opacity: (!selectedCategory || !text.trim()) ? 0.6 : 1, 
                      cursor: (!selectedCategory || !text.trim()) ? 'not-allowed' : 'pointer' 
                    }}
                  >
                    Отправить заявку
                  </button>
                </motion.div>
              )}

              {/* VIEW 2: CHAT PIPELINE */}
              {view === 'chat' && (
                <motion.div 
                  key="chat"
                  className="view-chat-layout"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* LEFT: CHAT UI */}
                  <div className="chat-panel">
                    <div className="ticket-header">
                      <div className="ticket-id">Заявка #4091</div>
                      <div className={`ticket-status \${step >= 4 ? 'resolved' : 'inprogress'}`}>
                        {step >= 4 ? 'Решено' : 'В работе'}
                      </div>
                    </div>
                    <h4 className="ticket-title">{text || activeScenario.title}</h4>

                    {hasImage && (
                      <div className="ticket-attached-image">
                        <img src={activeScenario.scenarioImage} alt="Attached" />
                      </div>
                    )}

                    <div className="chat-thread">
                      {/* Manager Assigned */}
                      <AnimatePresence>
                        {step >= 1 && (
                          <motion.div 
                            className="msg-system"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          >
                            Заявка принята отделом <strong>{activeScenario.category}</strong>. 
                            Ответственный: <strong>{activeScenario.manager}</strong>.
                          </motion.div>
                        )}

                        {/* Question from Manager */}
                        {step >= 2 && (
                          <motion.div 
                            className="msg-manager"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          >
                            <img className="avatar-img" src={activeScenario.managerAvatar} alt="Manager" />
                            <div className="bubble">
                              <p className="name">{activeScenario.manager}</p>
                              <p>{activeScenario.question}</p>
                            </div>
                          </motion.div>
                        )}

                        {/* Interactive User Choices */}
                        {step === 2 && (
                          <motion.div 
                            className="msg-choices"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          >
                            {activeScenario.answers.map((ans, idx) => (
                              <button key={idx} onClick={() => handleUserAnswer(idx)}>
                                {ans}
                              </button>
                            ))}
                          </motion.div>
                        )}

                        {/* User Answered */}
                        {step >= 3 && userAnswerIndex !== null && (
                          <motion.div 
                            className="msg-user"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          >
                            <div className="bubble">{activeScenario.answers[userAnswerIndex]}</div>
                          </motion.div>
                        )}

                        {/* Manager Reply */}
                        {step >= 3 && userAnswerIndex !== null && (
                          <motion.div 
                            className="msg-manager"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                          >
                            <img className="avatar-img" src={activeScenario.managerAvatar} alt="Manager" />
                            <div className="bubble">
                              <p className="name">{activeScenario.manager}</p>
                              <p>{activeScenario.replies[userAnswerIndex]}</p>
                            </div>
                          </motion.div>
                        )}

                        {/* Resolved & Feedback */}
                        {step >= 4 && (
                          <motion.div 
                            className="msg-system highlight"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          >
                            {/* @ts-ignore */}
                            <FaCheckCircle className="icon" />
                            <div>
                              <strong>Заявка успешно закрыта!</strong>
                              <p>Сотрудник завершил работу.</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* RATING BLOCK */}
                    <AnimatePresence>
                      {step >= 4 && (
                        <motion.div 
                          className="rating-block"
                          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        >
                          <p>Оцените решение заявки:</p>
                          <div className="stars">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button 
                                key={star} 
                                onClick={() => { setRating(star); setStep(5); }}
                                className={rating >= star ? 'active' : ''}
                              >
                                {/* @ts-ignore */}
                                <FaStar />
                              </button>
                            ))}
                          </div>
                          
                          {step >= 5 && (
                            <motion.button 
                              className="btn-show-dashboard"
                              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                              onClick={() => setView('dashboard')}
                            >
                              Посмотреть системный Dashboard ⟶
                            </motion.button>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* RIGHT: TIMELINE */}
                  <div className="timeline-panel">
                    <h3 className="timeline-title">Трекинг пайплайна</h3>
                    <p className="timeline-subtitle">Как это видит контролер</p>
                    
                    <div className="timeline">
                      {timelineSteps.map((label, idx) => {
                        const isActive = step >= idx;
                        const isCurrent = step === idx;
                        return (
                          <div key={idx} className={`tl-node \${isActive ? 'active' : ''} \${isCurrent ? 'current' : ''}`}>
                            <div className="tl-dot"><div className="tl-pulse"></div></div>
                            <div className="tl-text">
                              <span className="label">{label}</span>
                              {isActive && <span className="time">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* VIEW 3: DASHBOARD */}
              {view === 'dashboard' && (
                <motion.div 
                  key="dashboard"
                  className="view-dashboard"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="dash-header">
                    {/* @ts-ignore */}
                    <h2><FaChartBar /> Еженедельный отчет по компании</h2>
                    <button className="btn-restart" onClick={resetAll}>
                       {/* @ts-ignore */}
                      <FaRedo /> Перезапустить симуляцию
                    </button>
                  </div>

                  <div className="dash-metrics">
                    <div className="metric-card">
                      {/* @ts-ignore */}
                      <FaCommentDots className="m-icon" />
                      <div className="m-data">
                        <span>Всего заявок</span>
                        <strong>142</strong>
                      </div>
                    </div>
                    <div className="metric-card">
                      {/* @ts-ignore */}
                      <FaCheck className="m-icon success" />
                      <div className="m-data">
                        <span>С ответами (В работе)</span>
                        <strong>138</strong>
                      </div>
                    </div>
                    <div className="metric-card">
                      {/* @ts-ignore */}
                      <FaClock className="m-icon warning" />
                      <div className="m-data">
                        <span>Среднее время ответа</span>
                        <strong>12 минут</strong>
                      </div>
                    </div>
                    <div className="metric-card">
                      {/* @ts-ignore */}
                      <FaThumbsUp className="m-icon primary" />
                      <div className="m-data">
                        <span>Удовлетворенность</span>
                        <strong>98.5%</strong>
                      </div>
                    </div>
                  </div>

                  <div className="dash-charts">
                    <div className="chart-box">
                      <h3>Удовлетворенность (NPS) за 7 дней</h3>
                      <div className="fake-chart">
                        {/* Simple CSS bars */}
                        {[60, 75, 80, 95, 90, 98, 100].map((h, i) => (
                          <div key={i} className="bar-wrapper">
                            <div className="bar" style={{ height: `${h}%` }}></div>
                          </div>
                        ))}
                      </div>
                      <div className="chart-labels">
                        <span>Пн</span><span>Вт</span><span>Ср</span><span>Чт</span><span>Пт</span><span>Сб</span><span>Вс</span>
                      </div>
                    </div>
                    <div className="chart-box">
                      <h3>Загрузка отделов (Кол-во заявок)</h3>
                      <div className="horizontal-bars">
                        <div className="hbar"><span className="L">IT Отдел</span><div className="B"><div className="fill" style={{width: '80%', background: '#3b82f6'}}></div></div><span className="R">64</span></div>
                        <div className="hbar"><span className="L">АХО</span><div className="B"><div className="fill" style={{width: '45%', background: '#10b981'}}></div></div><span className="R">36</span></div>
                        <div className="hbar"><span className="L">HR</span><div className="B"><div className="fill" style={{width: '25%', background: '#f59e0b'}}></div></div><span className="R">20</span></div>
                        <div className="hbar"><span className="L">Бухгалтерия</span><div className="B"><div className="fill" style={{width: '30%', background: '#5cd5f6'}}></div></div><span className="R">22</span></div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
